// Safe, lazy IndexedDB access.
// Avoids importing Dexie at module scope and no-ops when IndexedDB is unavailable.

import { getCapabilities } from "./capabilities";

let dbPromise = null;
let warnedUnavailable = false;

const warnOnce = (message, err) => {
  if (warnedUnavailable) return;
  warnedUnavailable = true;
  if (err) {
    console.warn(message, err?.message || err);
  } else {
    console.warn(message);
  }
};

const loadDb = async () => {
  const { hasIndexedDB, isLegacyIOS } = getCapabilities();
  if (!hasIndexedDB || isLegacyIOS) {
    warnOnce("[dbSafe] IndexedDB disabled for this device; using in-memory fallbacks only.");
    return null;
  }
  if (!dbPromise) {
    dbPromise = import("./db");
  }
  try {
    return await dbPromise;
  } catch (err) {
    warnOnce("[dbSafe] Failed to load IndexedDB module; disabling cache.", err);
    dbPromise = null;
    return null;
  }
};

const withDb = async (fallback, fn) => {
  try {
    const mod = await loadDb();
    if (!mod) return fallback;
    return await fn(mod);
  } catch (err) {
    warnOnce("[dbSafe] IndexedDB call failed; disabling cache.", err);
    return fallback;
  }
};

export async function cacheBands(bands) {
  return withDb(undefined, (mod) => mod.cacheBands(bands));
}

export async function getCachedBands() {
  return withDb([], (mod) => mod.getCachedBands());
}

export async function cacheServices(services, bandId) {
  return withDb(undefined, (mod) => mod.cacheServices(services, bandId));
}

export async function getCachedServices(bandId) {
  return withDb([], (mod) => mod.getCachedServices(bandId));
}

export async function cacheSongs(songs, bandId) {
  return withDb(undefined, (mod) => mod.cacheSongs(songs, bandId));
}

export async function getCachedSongs(bandId) {
  return withDb([], (mod) => mod.getCachedSongs(bandId));
}

export async function cacheSetlist(serviceId, serviceSongs) {
  return withDb(undefined, (mod) => mod.cacheSetlist(serviceId, serviceSongs));
}

export async function getCachedSetlist(serviceId) {
  return withDb([], (mod) => mod.getCachedSetlist(serviceId));
}

export async function cacheSongSections(songId, sections) {
  return withDb(undefined, (mod) => mod.cacheSongSections(songId, sections));
}

export async function getCachedSongSections(songId) {
  return withDb([], (mod) => mod.getCachedSongSections(songId));
}

export async function clearCachedSongSections(songId) {
  return withDb(undefined, (mod) => mod.clearCachedSongSections(songId));
}

export async function cacheArrangement(arrangement, arrangementSections) {
  return withDb(undefined, (mod) => mod.cacheArrangement(arrangement, arrangementSections));
}

export async function getCachedArrangement(songId, bandId, serviceId = null) {
  return withDb(null, (mod) => mod.getCachedArrangement(songId, bandId, serviceId));
}

export async function getCachedArrangementSections(arrangementId) {
  return withDb([], (mod) => mod.getCachedArrangementSections(arrangementId));
}

export async function clearCachedArrangementsForSong(songId) {
  return withDb(undefined, (mod) => mod.clearCachedArrangementsForSong(songId));
}

export async function saveCanvasNotes(songId, dataURL) {
  return withDb(undefined, (mod) => mod.saveCanvasNotes(songId, dataURL));
}

export async function getCanvasNotes(songId) {
  return withDb(null, (mod) => mod.getCanvasNotes(songId));
}

export async function clearCanvasNotes(songId) {
  return withDb(undefined, (mod) => mod.clearCanvasNotes(songId));
}

export async function savePreference(key, value) {
  return withDb(undefined, (mod) => mod.savePreference(key, value));
}

export async function getPreference(key, defaultValue = null) {
  return withDb(defaultValue, (mod) => mod.getPreference(key, defaultValue));
}

export async function isDataStale(tableName, identifier, maxAge = 24 * 60 * 60 * 1000) {
  return withDb(true, (mod) => mod.isDataStale(tableName, identifier, maxAge));
}

export async function clearAllCache() {
  return withDb(undefined, (mod) => mod.clearAllCache());
}

export async function cacheSongStemsMapping(songId, mapping) {
  return withDb(undefined, (mod) => mod.cacheSongStemsMapping(songId, mapping));
}

export async function getCachedStemsMapping(songId) {
  return withDb(null, (mod) => mod.getCachedStemsMapping(songId));
}

export async function cacheStemsTracks(packageId, tracks) {
  return withDb(undefined, (mod) => mod.cacheStemsTracks(packageId, tracks));
}

export async function getCachedStemsTracks(packageId) {
  return withDb([], (mod) => mod.getCachedStemsTracks(packageId));
}

export async function clearCachedStemsTracks(packageId) {
  return withDb(undefined, (mod) => mod.clearCachedStemsTracks(packageId));
}

export async function cacheStemFile(packageId, fileName, blob) {
  return withDb(undefined, (mod) => mod.cacheStemFile(packageId, fileName, blob));
}

export async function getCachedStemFile(packageId, fileName) {
  return withDb(null, (mod) => mod.getCachedStemFile(packageId, fileName));
}

export async function clearCachedStemFiles(packageId) {
  return withDb(undefined, (mod) => mod.clearCachedStemFiles(packageId));
}

export async function saveStemWaveform(packageId, waveform) {
  return withDb(undefined, (mod) => mod.saveStemWaveform(packageId, waveform));
}

export async function getStemWaveform(packageId) {
  return withDb(null, (mod) => mod.getStemWaveform(packageId));
}

export async function cachePadFile(key, blob) {
  return withDb(undefined, (mod) => mod.cachePadFile(key, blob));
}

export async function getCachedPadFile(key) {
  return withDb(null, (mod) => mod.getCachedPadFile(key));
}

export async function cacheGuideTimeline(arrangementId, timeline) {
  return withDb(undefined, (mod) => mod.cacheGuideTimeline(arrangementId, timeline));
}

export async function getCachedGuideTimeline(arrangementId) {
  return withDb(null, (mod) => mod.getCachedGuideTimeline(arrangementId));
}

export async function saveStemUserMix(userId, packageId, trackId, mix) {
  return withDb(undefined, (mod) => mod.saveStemUserMix(userId, packageId, trackId, mix));
}

export async function getStemUserMix(userId, packageId) {
  return withDb([], (mod) => mod.getStemUserMix(userId, packageId));
}
